import React, { useEffect, useState } from 'react';
import {
  FiActivity,
  FiAward,
  FiBarChart2,
  FiCpu,
  FiGitMerge,
  FiMessageCircle,
  FiSearch,
  FiSun,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiZap
} from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import API from '../../utils/api';
import styles from './AdminResultInsights.module.css';

const ROLE_FULL = { RSM: 'Regional Sales Manager', FSM: 'Field Sales Manager', TSE: 'Territory Sales Manager' };

function getGradeColor(g) {
  return { A: '#1a5c38', B: '#27ae60', C: '#e67e22', D: '#e67e22', F: '#c0392b' }[g] || '#6b6b6b';
}

function InsightCard({ icon: Icon, title, value, description, color }) {
  return (
    <div className={styles.insightCard}>
      <div className={styles.insightIcon} style={{ background: color + '20', color: color }}>
        <Icon aria-hidden="true" />
      </div>
      <div className={styles.insightContent}>
        <div className={styles.insightTitle}>{title}</div>
        <div className={styles.insightValue} style={{ color }}>{value}</div>
        <div className={styles.insightDesc}>{description}</div>
      </div>
    </div>
  );
}

function LevelBar({ label, level, color }) {
  const levels = ['Low', 'Moderate', 'High', 'Advanced'];
  const levelIdx = levels.indexOf(level);
  return (
    <div className={styles.levelBarItem}>
      <div className={styles.levelLabel}>{label}</div>
      <div className={styles.levelBarContainer}>
        <div className={styles.levelBarTrack}>
          {levels.map((l, i) => (
            <div 
              key={i} 
              className={styles.levelSegment}
              style={{ 
                background: i <= levelIdx ? color : '#e0e0e0',
                flex: 1
              }}
            />
          ))}
        </div>
        <div className={styles.levelText}>{level}</div>
      </div>
    </div>
  );
}

export default function AdminResultInsights() {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/admin/candidates/${idOrSlug}`)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [idOrSlug]);

  if (loading) return <AdminLayout><div className={styles.loading}>Loading insights...</div></AdminLayout>;
  if (!data?.assessment) return (
    <AdminLayout>
      <div className={styles.noResult}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <p>No assessment data found.</p>
      </div>
    </AdminLayout>
  );

  const { candidate, assessment: a } = data;
  const getSection = key => a.sections?.find(s => s.section === key);
  const cog = getSection('cognitive');
  const per = getSection('personality');
  const lea = getSection('leadership');
  const cas = getSection('casestudy');
  const cp = a.competencyProfile || {};

  // Calculate insights
  const cogLevel = cog?.percentage >= 80 ? 'Advanced' : cog?.percentage >= 65 ? 'High' : cog?.percentage >= 50 ? 'Moderate' : 'Low';
  const leadLevel = lea?.percentage >= 80 ? 'Advanced' : lea?.percentage >= 65 ? 'High' : lea?.percentage >= 50 ? 'Moderate' : 'Low';
  const techLevel = cas?.percentage >= 80 ? 'Advanced' : cas?.percentage >= 65 ? 'High' : cas?.percentage >= 50 ? 'Moderate' : 'Low';

  const avgPersonality = (cp.achievementDrive + cp.resilienceAdaptability + cp.empathyInterpersonal + cp.teamLeadership + cp.analyticalThinking + cp.initiativeDrive + cp.integrityEthics + cp.strategicOrientation) / 8 || 0;
  const emotionalMaturity = avgPersonality >= 80 ? 'Highly Mature' : avgPersonality >= 65 ? 'Mature' : avgPersonality >= 50 ? 'Developing' : 'Needs Development';

  // Profile consistency
  const scores = [cog?.percentage || 0, per?.percentage || 0, lea?.percentage || 0, cas?.percentage || 0];
  const avgScore = scores.reduce((a, b) => a + b) / scores.length;
  const maxScore = Math.max(...scores);
  const minScore = Math.min(...scores);
  const consistency = maxScore - minScore <= 15 ? 'High' : maxScore - minScore <= 25 ? 'Moderate' : 'Low';

  // Decision making style based on personality scores
  const decisionStyle = cp.analyticalThinking > 80 ? 'Data-Driven & Deliberate' 
    : cp.initiativeDrive > 80 ? 'Quick & Action-Oriented'
    : cp.teamLeadership > 80 ? 'Collaborative & Consensus-Based'
    : 'Balanced Approach';

  // Readiness for role
  const roleReadiness = a.totalPercentage >= 80 ? 'Exceptional' : a.totalPercentage >= 70 ? 'Ready' : a.totalPercentage >= 60 ? 'Conditional' : 'Not Ready';
  const readinessColor = roleReadiness === 'Exceptional' ? '#1a5c38' : roleReadiness === 'Ready' ? '#27ae60' : roleReadiness === 'Conditional' ? '#e67e22' : '#c0392b';

  // Strengths and development areas
  const compItems = [
    { label: 'Achievement Drive', value: cp.achievementDrive },
    { label: 'Resilience & Adaptability', value: cp.resilienceAdaptability },
    { label: 'Empathy & Interpersonal', value: cp.empathyInterpersonal },
    { label: 'Team Leadership', value: cp.teamLeadership },
    { label: 'Analytical Thinking', value: cp.analyticalThinking },
    { label: 'Initiative & Drive', value: cp.initiativeDrive },
    { label: 'Integrity & Ethics', value: cp.integrityEthics },
    { label: 'Strategic Orientation', value: cp.strategicOrientation },
  ];
  
  const topStrengths = [...compItems].sort((a, b) => b.value - a.value).slice(0, 3);
  const devAreas = [...compItems].sort((a, b) => a.value - b.value).slice(0, 3);

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.topBar}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back to Result</button>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Detailed Assessment Insights</h1>
            <p className={styles.subtitle}>{candidate.fullName} • {candidate.role} — {ROLE_FULL[candidate.role]}</p>
          </div>
        </div>

        {/* Section 1: Cognitive Assessment */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Section 1: Cognitive & Analytical Skills</h2>
          <div className={styles.insightGrid}>
            <InsightCard 
              icon={FiCpu}
              title="Cognitive Complexity Level"
              value={cogLevel}
              description={`Score: ${cog?.percentage}% (${cog?.rawScore}/100)`}
              color={getGradeColor(cog?.grade)}
            />
            <InsightCard 
              icon={FiZap}
              title="Problem-Solving Ability"
              value={cog?.percentage >= 75 ? 'Strong' : cog?.percentage >= 55 ? 'Moderate' : 'Developing'}
              description="Based on MCQ performance and accuracy patterns"
              color={cog?.percentage >= 75 ? '#1a5c38' : cog?.percentage >= 55 ? '#e67e22' : '#c0392b'}
            />
            <InsightCard 
              icon={FiTarget}
              title="Logical Reasoning"
              value={cog?.percentage >= 70 ? 'High' : cog?.percentage >= 50 ? 'Moderate' : 'Low'}
              description="Ability to follow sequential logic and solve complex problems"
              color={cog?.percentage >= 70 ? '#1a5c38' : cog?.percentage >= 50 ? '#e67e22' : '#c0392b'}
            />
          </div>
        </section>

        {/* Section 2: Personality & EQ */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Section 2: Personality & Emotional Intelligence</h2>
          <div className={styles.insightGrid}>
            <InsightCard 
              icon={FiSun}
              title="Emotional Maturity Index"
              value={`${avgPersonality.toFixed(0)}%`}
              description={emotionalMaturity}
              color={avgPersonality >= 75 ? '#1a5c38' : avgPersonality >= 60 ? '#e67e22' : '#c0392b'}
            />
            <InsightCard 
              icon={FiUsers}
              title="Interpersonal Dynamics"
              value={cp.empathyInterpersonal + cp.teamLeadership >= 150 ? 'Strong' : 'Moderate'}
              description={`Empathy (${cp.empathyInterpersonal}%) + Team Leadership (${cp.teamLeadership}%)`}
              color={cp.empathyInterpersonal + cp.teamLeadership >= 150 ? '#1a5c38' : '#e67e22'}
            />
            <InsightCard 
              icon={FiGitMerge}
              title="Decision-Making Style"
              value={decisionStyle}
              description="Reveals how they approach decisions and problems"
              color="#2196F3"
            />
          </div>
          <div style={{ marginTop: '1.5rem' }}>
            <h3 className={styles.subsectionTitle}>Top Strengths (Personality)</h3>
            <div className={styles.strengthsList}>
              {topStrengths.map((item, idx) => (
                <div key={idx} className={styles.strengthItem}>
                  <span className={styles.strengthRank}>#{idx + 1}</span>
                  <span className={styles.strengthLabel}>{item.label}</span>
                  <span className={styles.strengthValue} style={{ color: '#1a5c38' }}>{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <h3 className={styles.subsectionTitle}>Development Areas (Personality)</h3>
            <div className={styles.strengthsList}>
              {devAreas.map((item, idx) => (
                <div key={idx} className={styles.strengthItem} style={{ background: 'rgba(192, 57, 43, 0.04)' }}>
                  <span className={styles.strengthRank} style={{ color: '#c0392b' }}><FiTarget aria-hidden="true" /></span>
                  <span className={styles.strengthLabel}>{item.label}</span>
                  <span className={styles.strengthValue} style={{ color: '#c0392b' }}>{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: Leadership */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Section 3: Leadership & Team Management</h2>
          <div className={styles.insightGrid}>
            <InsightCard 
              icon={FiAward}
              title="Leadership Readiness"
              value={leadLevel}
              description={`Score: ${lea?.percentage}% (${lea?.rawScore}/100)`}
              color={getGradeColor(lea?.grade)}
            />
            <InsightCard 
              icon={FiUsers}
              title="Team Collaboration"
              value={lea?.percentage >= 75 ? 'High' : lea?.percentage >= 55 ? 'Moderate' : 'Developing'}
              description="Capability to lead and manage team dynamics"
              color={lea?.percentage >= 75 ? '#1a5c38' : lea?.percentage >= 55 ? '#e67e22' : '#c0392b'}
            />
            <InsightCard 
              icon={FiBarChart2}
              title="Scenario-Based Judgment"
              value={lea?.percentage >= 70 ? 'Strong' : lea?.percentage >= 50 ? 'Adequate' : 'Needs Work'}
              description="Quality of decisions in realistic leadership scenarios"
              color={lea?.percentage >= 70 ? '#1a5c38' : lea?.percentage >= 50 ? '#e67e22' : '#c0392b'}
            />
          </div>
        </section>

        {/* Section 4: Technical Case Study */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Section 4: Technical Case Study & Business Acumen</h2>
          <div className={styles.insightGrid}>
            <InsightCard 
              icon={FiTrendingUp}
              title="Business Acumen"
              value={techLevel}
              description={`Score: ${cas?.percentage}% (${cas?.rawScore}/100)`}
              color={getGradeColor(cas?.grade)}
            />
            <InsightCard 
              icon={FiSearch}
              title="Data Interpretation Ability"
              value={cas?.percentage >= 75 ? 'Strong' : cas?.percentage >= 50 ? 'Moderate' : 'Limited'}
              description="Ability to analyze sales/business data accurately"
              color={cas?.percentage >= 75 ? '#1a5c38' : cas?.percentage >= 50 ? '#e67e22' : '#c0392b'}
            />
            <InsightCard 
              icon={FiMessageCircle}
              title="Strategic Application"
              value={cas?.percentage >= 70 ? 'Applied Well' : cas?.percentage >= 50 ? 'Partial' : 'Limited'}
              description="How effectively strategic thinking translates to practical business problems"
              color={cas?.percentage >= 70 ? '#1a5c38' : cas?.percentage >= 50 ? '#e67e22' : '#c0392b'}
            />
          </div>
        </section>

        {/* Cross-Section Analysis */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Cross-Section Analysis</h2>
          
          {/* Profile Consistency */}
          <div className={styles.analysisCard}>
            <h3 className={styles.analysisTitle}>Performance Consistency Score: {consistency}</h3>
            <p className={styles.analysisDesc}>
              {consistency === 'High' 
                ? 'Candidate shows consistent performance across all sections, indicating balanced capabilities.' 
                : consistency === 'Moderate'
                ? 'Candidate shows varied performance; some areas are stronger than others. Focus development on weaker sections.'
                : 'Candidate shows significant variation; strong in some areas but notable gaps in others. Targeted development recommended.'}
            </p>
            <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.8rem' }}>
              {[
                { label: 'Cognitive', value: cog?.percentage || 0 },
                { label: 'Personality', value: per?.percentage || 0 },
                { label: 'Leadership', value: lea?.percentage || 0 },
                { label: 'Case Study', value: cas?.percentage || 0 }
              ].map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: '700', color: getGradeColor(s.value >= 80 ? 'A' : s.value >= 70 ? 'B' : s.value >= 60 ? 'C' : s.value >= 50 ? 'D' : 'F') }}>
                    {s.value}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: '0.3rem' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Role Readiness */}
          <div className={styles.analysisCard} style={{ marginTop: '1rem' }}>
            <h3 className={styles.analysisTitle}>Overall Role Readiness: <span style={{ color: readinessColor }}>{roleReadiness}</span></h3>
            <p className={styles.analysisDesc}>
              {roleReadiness === 'Exceptional' 
                ? `${candidate.fullName} demonstrates exceptional capabilities across all dimensions and is highly ready for the ${candidate.role} role.`
                : roleReadiness === 'Ready'
                ? `${candidate.fullName} meets the threshold requirements and is ready for the ${candidate.role} role with confidence.`
                : roleReadiness === 'Conditional'
                ? `${candidate.fullName} partially meets requirements. Specific development in identified areas is recommended before full role deployment.`
                : `${candidate.fullName} does not currently meet the threshold for the ${candidate.role} role. Comprehensive development plan recommended.`}
            </p>
          </div>

          {/* Development Roadmap */}
          <div className={styles.analysisCard} style={{ marginTop: '1rem' }}>
            <h3 className={styles.analysisTitle}>Recommended Development Roadmap</h3>
            <div className={styles.roadmapList}>
              <div className={styles.roadmapItem}>
                <span className={styles.roadmapPhase}>Phase 1 (Immediate)</span>
                <p>
                  {devAreas[0] && `Focus on improving ${devAreas[0].label} (currently ${devAreas[0].value}%). This is the primary development area.`}
                </p>
              </div>
              <div className={styles.roadmapItem}>
                <span className={styles.roadmapPhase}>Phase 2 (30-60 Days)</span>
                <p>
                  {devAreas[1] && `Enhance ${devAreas[1].label} (currently ${devAreas[1].value}%) through targeted coaching and practice.`}
                </p>
              </div>
              <div className={styles.roadmapItem}>
                <span className={styles.roadmapPhase}>Phase 3 (60+ Days)</span>
                <p>
                  {devAreas[2] && `Build capability in ${devAreas[2].label} (currently ${devAreas[2].value}%) for sustained growth.`}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Potential Assessment */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Potential & Growth Ceiling</h2>
          <div className={styles.insightGrid}>
            <InsightCard 
              icon={FiActivity}
              title="Current Performance"
              value={`${a.totalPercentage}%`}
              description="Where the candidate stands today"
              color={getGradeColor(a.overallGrade)}
            />
            <InsightCard 
              icon={FiTrendingUp}
              title="Growth Potential"
              value={Math.max(...topStrengths.map(s => s.value)) >= 85 ? 'High' : Math.max(...topStrengths.map(s => s.value)) >= 70 ? 'Moderate' : 'Developing'}
              description="Based on strongest competencies and demonstrated capability"
              color="#2196F3"
            />
            <InsightCard 
              icon={FiTarget}
              title="Recommended Action"
              value={a.totalPercentage >= 70 ? 'Advance' : a.totalPercentage >= 60 ? 'Reassess' : 'Hold'}
              description="Next step in the hiring/development process"
              color={a.totalPercentage >= 70 ? '#1a5c38' : a.totalPercentage >= 60 ? '#e67e22' : '#c0392b'}
            />
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
